import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useFilms } from "../../context/FilmsContext";
import { Play } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.19, 1, 0.22, 1], delay: i * 0.08 },
  }),
};

const FilmCard = ({ film, index, className = "" }) => {
  const navigate = useNavigate();
  return (
  <motion.article
    data-testid={`film-card-${film.id}`}
    onClick={() => navigate(`/watch/${film.id}`)}
    custom={index}
    variants={reveal}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    className={`group relative overflow-hidden border border-white/10 hover:border-blood/60 transition-colors duration-500 cursor-pointer ${className}`}
  >
    <img
      src={film.poster}
      alt={`${film.title} poster`}
      className="absolute inset-0 h-full w-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-110 transition-all duration-[900ms] ease-out"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-90 group-hover:opacity-80 transition-opacity duration-500" />

    <div className="relative h-full flex flex-col justify-between p-5 md:p-6">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-blood border border-blood/40 px-2 py-1">
          {film.category}
        </span>
        <span className="font-mono text-[10px] tracking-[0.2em] text-bone/70">
          {film.year}
        </span>
      </div>

      <div>
        <h3 className="font-serif text-2xl md:text-3xl leading-none text-bone tracking-tight">
          {film.title}
        </h3>
        <p className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
          {film.director} · {film.runtime}
        </p>
        <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-out">
          <div className="overflow-hidden">
            <p className="mt-3 text-sm text-bone/80 leading-relaxed max-w-sm">
              {film.blurb}
            </p>
            <span className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-blood">
              <Play className="h-3 w-3 fill-current" /> Watch now
            </span>
          </div>
        </div>
      </div>
    </div>
  </motion.article>
  );
};

export const FeaturedFilms = () => {
  const { films } = useFilms();
  const featured = films.slice(0, 6);
  return (
    <section
      id="featured"
      data-testid="featured-section"
      className="relative py-24 md:py-36 px-5 md:px-10"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <span className="font-mono text-[11px] tracking-[0.4em] uppercase text-blood">
              (Now Streaming)
            </span>
            <h2 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.9] tracking-tighter text-bone">
              The Featured
              <br />
              <span className="italic">Cabinet</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm md:text-base text-muted-foreground leading-relaxed md:text-right">
            Six restorations to begin with — the films that taught the world to
            be afraid, and to dream of other worlds.
          </p>
        </div>

        {/* Asymmetric bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 auto-rows-[minmax(340px,auto)]">
          <FilmCard film={featured[0]} index={0} className="md:col-span-5 md:row-span-2" />
          <FilmCard film={featured[1]} index={1} className="md:col-span-7" />
          <FilmCard film={featured[2]} index={2} className="md:col-span-4" />
          <FilmCard film={featured[3]} index={3} className="md:col-span-3" />
          <FilmCard film={featured[4]} index={4} className="md:col-span-7 md:row-span-2" />
          <FilmCard film={featured[5]} index={5} className="md:col-span-5" />
        </div>
      </div>
    </section>
  );
};
